export interface InvisibleCharacter {
  code: number;
  name: string;
  symbol: string;
  category: 'space' | 'control' | 'zero-width' | 'directional' | 'deprecated' | 'combining';
}

export interface DetectedCharacter {
  position: number;
  character: InvisibleCharacter;
  length: number;
}

// Comprehensive list of invisible Unicode characters (330+ characters)
// Organized by Unicode blocks for maintainability
export const INVISIBLE_CHARACTERS: InvisibleCharacter[] = [
  // ===== C0 CONTROL CHARACTERS (U+0000 - U+001F, U+007F) =====
  { code: 0x0000, name: 'Null', symbol: '␀', category: 'control' },
  { code: 0x0001, name: 'Start of Heading (SOH)', symbol: '⟨SOH⟩', category: 'control' },
  { code: 0x0002, name: 'Start of Text (STX)', symbol: '⟨STX⟩', category: 'control' },
  { code: 0x0003, name: 'End of Text (ETX)', symbol: '⟨ETX⟩', category: 'control' },
  { code: 0x0004, name: 'End of Transmission (EOT)', symbol: '⟨EOT⟩', category: 'control' },
  { code: 0x0005, name: 'Enquiry (ENQ)', symbol: '⟨ENQ⟩', category: 'control' },
  { code: 0x0006, name: 'Acknowledge (ACK)', symbol: '⟨ACK⟩', category: 'control' },
  { code: 0x0007, name: 'Bell (BEL)', symbol: '⟨BEL⟩', category: 'control' },
  { code: 0x0008, name: 'Backspace (BS)', symbol: '⟨BS⟩', category: 'control' },
  { code: 0x0009, name: 'Tab', symbol: '→', category: 'control' },
  { code: 0x000A, name: 'Line Feed', symbol: '↵', category: 'control' },
  { code: 0x000B, name: 'Vertical Tab (VT)', symbol: '⟨VT⟩', category: 'control' },
  { code: 0x000C, name: 'Form Feed (FF)', symbol: '⟨FF⟩', category: 'control' },
  { code: 0x000D, name: 'Carriage Return', symbol: '⏎', category: 'control' },
  { code: 0x000E, name: 'Shift Out (SO)', symbol: '⟨SO⟩', category: 'control' },
  { code: 0x000F, name: 'Shift In (SI)', symbol: '⟨SI⟩', category: 'control' },
  { code: 0x0010, name: 'Data Link Escape (DLE)', symbol: '⟨DLE⟩', category: 'control' },
  { code: 0x0011, name: 'Device Control 1 (DC1)', symbol: '⟨DC1⟩', category: 'control' },
  { code: 0x0012, name: 'Device Control 2 (DC2)', symbol: '⟨DC2⟩', category: 'control' },
  { code: 0x0013, name: 'Device Control 3 (DC3)', symbol: '⟨DC3⟩', category: 'control' },
  { code: 0x0014, name: 'Device Control 4 (DC4)', symbol: '⟨DC4⟩', category: 'control' },
  { code: 0x0015, name: 'Negative Acknowledge (NAK)', symbol: '⟨NAK⟩', category: 'control' },
  { code: 0x0016, name: 'Synchronous Idle (SYN)', symbol: '⟨SYN⟩', category: 'control' },
  { code: 0x0017, name: 'End of Transmission Block (ETB)', symbol: '⟨ETB⟩', category: 'control' },
  { code: 0x0018, name: 'Cancel (CAN)', symbol: '⟨CAN⟩', category: 'control' },
  { code: 0x0019, name: 'End of Medium (EM)', symbol: '⟨EM⟩', category: 'control' },
  { code: 0x001A, name: 'Substitute (SUB)', symbol: '⟨SUB⟩', category: 'control' },
  { code: 0x001B, name: 'Escape (ESC)', symbol: '⟨ESC⟩', category: 'control' },
  { code: 0x001C, name: 'File Separator (FS)', symbol: '⟨FS⟩', category: 'control' },
  { code: 0x001D, name: 'Group Separator (GS)', symbol: '⟨GS⟩', category: 'control' },
  { code: 0x001E, name: 'Record Separator (RS)', symbol: '⟨RS⟩', category: 'control' },
  { code: 0x001F, name: 'Unit Separator (US)', symbol: '⟨US⟩', category: 'control' },
  { code: 0x007F, name: 'Delete (DEL)', symbol: '⟨DEL⟩', category: 'control' },

  // ===== SPACE CHARACTERS =====
  { code: 0x0020, name: 'Space', symbol: '•', category: 'space' },
  { code: 0x00A0, name: 'No-Break Space', symbol: '⍽', category: 'space' },
  { code: 0x1680, name: 'Ogham Space Mark', symbol: '⟨OSM⟩', category: 'space' },
  { code: 0x2000, name: 'En Quad', symbol: '⟨NQSP⟩', category: 'space' },
  { code: 0x2001, name: 'Em Quad', symbol: '⟨MQSP⟩', category: 'space' },
  { code: 0x2002, name: 'En Space', symbol: '⟨ENSP⟩', category: 'space' },
  { code: 0x2003, name: 'Em Space', symbol: '⟨EMSP⟩', category: 'space' },
  { code: 0x2004, name: 'Three-Per-Em Space', symbol: '⟨3/M⟩', category: 'space' },
  { code: 0x2005, name: 'Four-Per-Em Space', symbol: '⟨4/M⟩', category: 'space' },
  { code: 0x2006, name: 'Six-Per-Em Space', symbol: '⟨6/M⟩', category: 'space' },
  { code: 0x2007, name: 'Figure Space', symbol: '⟨FSP⟩', category: 'space' },
  { code: 0x2008, name: 'Punctuation Space', symbol: '⟨PSP⟩', category: 'space' },
  { code: 0x2009, name: 'Thin Space', symbol: '⟨THSP⟩', category: 'space' },
  { code: 0x200A, name: 'Hair Space', symbol: '⟨HSP⟩', category: 'space' },
  { code: 0x202F, name: 'Narrow No-Break Space', symbol: '⟨NNBSP⟩', category: 'space' },
  { code: 0x205F, name: 'Medium Mathematical Space', symbol: '⟨MMSP⟩', category: 'space' },
  { code: 0x3000, name: 'Ideographic Space', symbol: '⟨IDSP⟩', category: 'space' },

  // ===== C1 CONTROL CHARACTERS (U+0080 - U+009F) =====
  { code: 0x0080, name: 'Padding Character (PAD)', symbol: '⟨PAD⟩', category: 'control' },
  { code: 0x0081, name: 'High Octet Preset (HOP)', symbol: '⟨HOP⟩', category: 'control' },
  { code: 0x0082, name: 'Break Permitted Here (BPH)', symbol: '⟨BPH⟩', category: 'control' },
  { code: 0x0083, name: 'No Break Here (NBH)', symbol: '⟨NBH⟩', category: 'control' },
  { code: 0x0084, name: 'Index (IND)', symbol: '⟨IND⟩', category: 'control' },
  { code: 0x0085, name: 'Next Line (NEL)', symbol: '⟨NEL⟩', category: 'control' },
  { code: 0x0086, name: 'Start of Selected Area (SSA)', symbol: '⟨SSA⟩', category: 'control' },
  { code: 0x0087, name: 'End of Selected Area (ESA)', symbol: '⟨ESA⟩', category: 'control' },
  { code: 0x0088, name: 'Character Tabulation Set (HTS)', symbol: '⟨HTS⟩', category: 'control' },
  { code: 0x0089, name: 'Character Tabulation with Justification (HTJ)', symbol: '⟨HTJ⟩', category: 'control' },
  { code: 0x008A, name: 'Line Tabulation Set (VTS)', symbol: '⟨VTS⟩', category: 'control' },
  { code: 0x008B, name: 'Partial Line Forward (PLD)', symbol: '⟨PLD⟩', category: 'control' },
  { code: 0x008C, name: 'Partial Line Backward (PLU)', symbol: '⟨PLU⟩', category: 'control' },
  { code: 0x008D, name: 'Reverse Line Feed (RI)', symbol: '⟨RI⟩', category: 'control' },
  { code: 0x008E, name: 'Single Shift Two (SS2)', symbol: '⟨SS2⟩', category: 'control' },
  { code: 0x008F, name: 'Single Shift Three (SS3)', symbol: '⟨SS3⟩', category: 'control' },
  { code: 0x0090, name: 'Device Control String (DCS)', symbol: '⟨DCS⟩', category: 'control' },
  { code: 0x0091, name: 'Private Use One (PU1)', symbol: '⟨PU1⟩', category: 'control' },
  { code: 0x0092, name: 'Private Use Two (PU2)', symbol: '⟨PU2⟩', category: 'control' },
  { code: 0x0093, name: 'Set Transmit State (STS)', symbol: '⟨STS⟩', category: 'control' },
  { code: 0x0094, name: 'Cancel Character (CCH)', symbol: '⟨CCH⟩', category: 'control' },
  { code: 0x0095, name: 'Message Waiting (MW)', symbol: '⟨MW⟩', category: 'control' },
  { code: 0x0096, name: 'Start of Guarded Area (SPA)', symbol: '⟨SPA⟩', category: 'control' },
  { code: 0x0097, name: 'End of Guarded Area (EPA)', symbol: '⟨EPA⟩', category: 'control' },
  { code: 0x0098, name: 'Start of String (SOS)', symbol: '⟨SOS⟩', category: 'control' },
  { code: 0x0099, name: 'Single Graphic Character Introducer (SGCI)', symbol: '⟨SGCI⟩', category: 'control' },
  { code: 0x009A, name: 'Single Character Introducer (SCI)', symbol: '⟨SCI⟩', category: 'control' },
  { code: 0x009B, name: 'Control Sequence Introducer (CSI)', symbol: '⟨CSI⟩', category: 'control' },
  { code: 0x009C, name: 'String Terminator (ST)', symbol: '⟨ST⟩', category: 'control' },
  { code: 0x009D, name: 'Operating System Command (OSC)', symbol: '⟨OSC⟩', category: 'control' },
  { code: 0x009E, name: 'Privacy Message (PM)', symbol: '⟨PM⟩', category: 'control' },
  { code: 0x009F, name: 'Application Program Command (APC)', symbol: '⟨APC⟩', category: 'control' },

  // ===== SOFT HYPHEN =====
  { code: 0x00AD, name: 'Soft Hyphen', symbol: '⟨SHY⟩', category: 'control' },

  // ===== COMBINING DIACRITICAL MARKS (U+0300-U+036F) - Zero-Width =====
  { code: 0x0300, name: 'Combining Grave Accent', symbol: '⟨◌̀⟩', category: 'combining' },
  { code: 0x0301, name: 'Combining Acute Accent', symbol: '⟨◌́⟩', category: 'combining' },
  { code: 0x0302, name: 'Combining Circumflex Accent', symbol: '⟨◌̂⟩', category: 'combining' },
  { code: 0x0303, name: 'Combining Tilde', symbol: '⟨◌̃⟩', category: 'combining' },
  { code: 0x0304, name: 'Combining Macron', symbol: '⟨◌̄⟩', category: 'combining' },
  { code: 0x0305, name: 'Combining Overline', symbol: '⟨◌̅⟩', category: 'combining' },
  { code: 0x0306, name: 'Combining Breve', symbol: '⟨◌̆⟩', category: 'combining' },
  { code: 0x0307, name: 'Combining Dot Above', symbol: '⟨◌̇⟩', category: 'combining' },
  { code: 0x0308, name: 'Combining Diaeresis', symbol: '⟨◌̈⟩', category: 'combining' },
  { code: 0x0309, name: 'Combining Hook Above', symbol: '⟨◌̉⟩', category: 'combining' },
  { code: 0x030A, name: 'Combining Ring Above', symbol: '⟨◌̊⟩', category: 'combining' },
  { code: 0x030B, name: 'Combining Double Acute Accent', symbol: '⟨◌̋⟩', category: 'combining' },
  { code: 0x030C, name: 'Combining Caron', symbol: '⟨◌̌⟩', category: 'combining' },
  { code: 0x030D, name: 'Combining Vertical Line Above', symbol: '⟨◌̍⟩', category: 'combining' },
  { code: 0x030E, name: 'Combining Double Vertical Line Above', symbol: '⟨◌̎⟩', category: 'combining' },
  { code: 0x030F, name: 'Combining Double Grave Accent', symbol: '⟨◌̏⟩', category: 'combining' },
  { code: 0x0310, name: 'Combining Candrabindu', symbol: '⟨◌̐⟩', category: 'combining' },
  { code: 0x0311, name: 'Combining Inverted Breve', symbol: '⟨◌̑⟩', category: 'combining' },
  { code: 0x0312, name: 'Combining Turned Comma Above', symbol: '⟨◌̒⟩', category: 'combining' },
  { code: 0x0313, name: 'Combining Comma Above', symbol: '⟨◌̓⟩', category: 'combining' },
  { code: 0x0314, name: 'Combining Reversed Comma Above', symbol: '⟨◌̔⟩', category: 'combining' },
  { code: 0x0315, name: 'Combining Comma Above Right', symbol: '⟨◌̕⟩', category: 'combining' },
  { code: 0x0316, name: 'Combining Grave Accent Below', symbol: '⟨◌̖⟩', category: 'combining' },
  { code: 0x0317, name: 'Combining Acute Accent Below', symbol: '⟨◌̗⟩', category: 'combining' },
  { code: 0x0318, name: 'Combining Left Tack Below', symbol: '⟨◌̘⟩', category: 'combining' },
  { code: 0x0319, name: 'Combining Right Tack Below', symbol: '⟨◌̙⟩', category: 'combining' },
  { code: 0x031A, name: 'Combining Left Angle Above', symbol: '⟨◌̚⟩', category: 'combining' },
  { code: 0x031B, name: 'Combining Horn', symbol: '⟨◌̛⟩', category: 'combining' },
  { code: 0x031C, name: 'Combining Left Half Ring Below', symbol: '⟨◌̜⟩', category: 'combining' },
  { code: 0x031D, name: 'Combining Up Tack Below', symbol: '⟨◌̝⟩', category: 'combining' },
  { code: 0x031E, name: 'Combining Down Tack Below', symbol: '⟨◌̞⟩', category: 'combining' },
  { code: 0x031F, name: 'Combining Plus Sign Below', symbol: '⟨◌̟⟩', category: 'combining' },
  { code: 0x0320, name: 'Combining Minus Sign Below', symbol: '⟨◌̠⟩', category: 'combining' },
  { code: 0x0321, name: 'Combining Palatalized Hook Below', symbol: '⟨◌̡⟩', category: 'combining' },
  { code: 0x0322, name: 'Combining Retroflex Hook Below', symbol: '⟨◌̢⟩', category: 'combining' },
  { code: 0x0323, name: 'Combining Dot Below', symbol: '⟨◌̣⟩', category: 'combining' },
  { code: 0x0324, name: 'Combining Diaeresis Below', symbol: '⟨◌̤⟩', category: 'combining' },
  { code: 0x0325, name: 'Combining Ring Below', symbol: '⟨◌̥⟩', category: 'combining' },
  { code: 0x0326, name: 'Combining Comma Below', symbol: '⟨◌̦⟩', category: 'combining' },
  { code: 0x0327, name: 'Combining Cedilla', symbol: '⟨◌̧⟩', category: 'combining' },
  { code: 0x0328, name: 'Combining Ogonek', symbol: '⟨◌̨⟩', category: 'combining' },
  { code: 0x0329, name: 'Combining Vertical Line Below', symbol: '⟨◌̩⟩', category: 'combining' },
  { code: 0x032A, name: 'Combining Bridge Below', symbol: '⟨◌̪⟩', category: 'combining' },
  { code: 0x032B, name: 'Combining Inverted Double Arch Below', symbol: '⟨◌̫⟩', category: 'combining' },
  { code: 0x032C, name: 'Combining Caron Below', symbol: '⟨◌̬⟩', category: 'combining' },
  { code: 0x032D, name: 'Combining Circumflex Accent Below', symbol: '⟨◌̭⟩', category: 'combining' },
  { code: 0x032E, name: 'Combining Breve Below', symbol: '⟨◌̮⟩', category: 'combining' },
  { code: 0x032F, name: 'Combining Inverted Breve Below', symbol: '⟨◌̯⟩', category: 'combining' },
  { code: 0x0330, name: 'Combining Tilde Below', symbol: '⟨◌̰⟩', category: 'combining' },
  { code: 0x0331, name: 'Combining Macron Below', symbol: '⟨◌̱⟩', category: 'combining' },
  { code: 0x0332, name: 'Combining Low Line', symbol: '⟨◌̲⟩', category: 'combining' },
  { code: 0x0333, name: 'Combining Double Low Line', symbol: '⟨◌̳⟩', category: 'combining' },
  { code: 0x0334, name: 'Combining Tilde Overlay', symbol: '⟨◌̴⟩', category: 'combining' },
  { code: 0x0335, name: 'Combining Short Stroke Overlay', symbol: '⟨◌̵⟩', category: 'combining' },
  { code: 0x0336, name: 'Combining Long Stroke Overlay', symbol: '⟨◌̶⟩', category: 'combining' },
  { code: 0x0337, name: 'Combining Short Solidus Overlay', symbol: '⟨◌̷⟩', category: 'combining' },
  { code: 0x0338, name: 'Combining Long Solidus Overlay', symbol: '⟨◌̸⟩', category: 'combining' },
  { code: 0x0339, name: 'Combining Right Half Ring Below', symbol: '⟨◌̹⟩', category: 'combining' },
  { code: 0x033A, name: 'Combining Inverted Bridge Below', symbol: '⟨◌̺⟩', category: 'combining' },
  { code: 0x033B, name: 'Combining Square Below', symbol: '⟨◌̻⟩', category: 'combining' },
  { code: 0x033C, name: 'Combining Seagull Below', symbol: '⟨◌̼⟩', category: 'combining' },
  { code: 0x033D, name: 'Combining X Above', symbol: '⟨◌̽⟩', category: 'combining' },
  { code: 0x033E, name: 'Combining Vertical Tilde', symbol: '⟨◌̾⟩', category: 'combining' },
  { code: 0x033F, name: 'Combining Double Overline', symbol: '⟨◌̿⟩', category: 'combining' },
  { code: 0x0340, name: 'Combining Grave Tone Mark', symbol: '⟨◌̀⟩', category: 'combining' },
  { code: 0x0341, name: 'Combining Acute Tone Mark', symbol: '⟨◌́⟩', category: 'combining' },
  { code: 0x0342, name: 'Combining Greek Perispomeni', symbol: '⟨◌͂⟩', category: 'combining' },
  { code: 0x0343, name: 'Combining Greek Koronis', symbol: '⟨◌̓⟩', category: 'combining' },
  { code: 0x0344, name: 'Combining Greek Dialytika Tonos', symbol: '⟨◌̈́⟩', category: 'combining' },
  { code: 0x0345, name: 'Combining Greek Ypogegrammeni', symbol: '⟨◌ͅ⟩', category: 'combining' },
  { code: 0x0346, name: 'Combining Bridge Above', symbol: '⟨◌͆⟩', category: 'combining' },
  { code: 0x0347, name: 'Combining Equals Sign Below', symbol: '⟨◌͇⟩', category: 'combining' },
  { code: 0x0348, name: 'Combining Double Vertical Line Below', symbol: '⟨◌͈⟩', category: 'combining' },
  { code: 0x0349, name: 'Combining Left Angle Below', symbol: '⟨◌͉⟩', category: 'combining' },
  { code: 0x034A, name: 'Combining Not Tilde Above', symbol: '⟨◌͊⟩', category: 'combining' },
  { code: 0x034B, name: 'Combining Homothetic Above', symbol: '⟨◌͋⟩', category: 'combining' },
  { code: 0x034C, name: 'Combining Almost Equal To Above', symbol: '⟨◌͌⟩', category: 'combining' },
  { code: 0x034D, name: 'Combining Left Right Arrow Below', symbol: '⟨◌͍⟩', category: 'combining' },
  { code: 0x034E, name: 'Combining Upwards Arrow Below', symbol: '⟨◌͎⟩', category: 'combining' },
  { code: 0x034F, name: 'Combining Grapheme Joiner', symbol: '⟨CGJ⟩', category: 'zero-width' },
  { code: 0x0350, name: 'Combining Right Arrowhead Above', symbol: '⟨◌͐⟩', category: 'combining' },
  { code: 0x0351, name: 'Combining Left Half Ring Above', symbol: '⟨◌͑⟩', category: 'combining' },
  { code: 0x0352, name: 'Combining Fermata', symbol: '⟨◌͒⟩', category: 'combining' },
  { code: 0x0353, name: 'Combining X Below', symbol: '⟨◌͓⟩', category: 'combining' },
  { code: 0x0354, name: 'Combining Left Arrowhead Below', symbol: '⟨◌͔⟩', category: 'combining' },
  { code: 0x0355, name: 'Combining Right Arrowhead Below', symbol: '⟨◌͕⟩', category: 'combining' },
  { code: 0x0356, name: 'Combining Right Arrowhead and Up Arrowhead Below', symbol: '⟨◌͖⟩', category: 'combining' },
  { code: 0x0357, name: 'Combining Right Half Ring Above', symbol: '⟨◌͗⟩', category: 'combining' },
  { code: 0x0358, name: 'Combining Dot Above Right', symbol: '⟨◌͘⟩', category: 'combining' },
  { code: 0x0359, name: 'Combining Asterisk Below', symbol: '⟨◌͙⟩', category: 'combining' },
  { code: 0x035A, name: 'Combining Double Ring Below', symbol: '⟨◌͚⟩', category: 'combining' },
  { code: 0x035B, name: 'Combining Zigzag Above', symbol: '⟨◌͛⟩', category: 'combining' },
  { code: 0x035C, name: 'Combining Double Breve Below', symbol: '⟨◌͜⟩', category: 'combining' },
  { code: 0x035D, name: 'Combining Double Breve', symbol: '⟨◌͝⟩', category: 'combining' },
  { code: 0x035E, name: 'Combining Double Macron', symbol: '⟨◌͞⟩', category: 'combining' },
  { code: 0x035F, name: 'Combining Double Macron Below', symbol: '⟨◌͟⟩', category: 'combining' },
  { code: 0x0360, name: 'Combining Double Tilde', symbol: '⟨◌͠⟩', category: 'combining' },
  { code: 0x0361, name: 'Combining Double Inverted Breve', symbol: '⟨◌͡⟩', category: 'combining' },
  { code: 0x0362, name: 'Combining Double Rightwards Arrow Below', symbol: '⟨◌͢⟩', category: 'combining' },
  { code: 0x0363, name: 'Combining Latin Small Letter A', symbol: '⟨◌ͣ⟩', category: 'combining' },
  { code: 0x0364, name: 'Combining Latin Small Letter E', symbol: '⟨◌ͤ⟩', category: 'combining' },
  { code: 0x0365, name: 'Combining Latin Small Letter I', symbol: '⟨◌ͥ⟩', category: 'combining' },
  { code: 0x0366, name: 'Combining Latin Small Letter O', symbol: '⟨◌ͦ⟩', category: 'combining' },
  { code: 0x0367, name: 'Combining Latin Small Letter U', symbol: '⟨◌ͧ⟩', category: 'combining' },
  { code: 0x0368, name: 'Combining Latin Small Letter C', symbol: '⟨◌ͨ⟩', category: 'combining' },
  { code: 0x0369, name: 'Combining Latin Small Letter D', symbol: '⟨◌ͩ⟩', category: 'combining' },
  { code: 0x036A, name: 'Combining Latin Small Letter H', symbol: '⟨◌ͪ⟩', category: 'combining' },
  { code: 0x036B, name: 'Combining Latin Small Letter M', symbol: '⟨◌ͫ⟩', category: 'combining' },
  { code: 0x036C, name: 'Combining Latin Small Letter R', symbol: '⟨◌ͬ⟩', category: 'combining' },
  { code: 0x036D, name: 'Combining Latin Small Letter T', symbol: '⟨◌ͭ⟩', category: 'combining' },
  { code: 0x036E, name: 'Combining Latin Small Letter V', symbol: '⟨◌ͮ⟩', category: 'combining' },
  { code: 0x036F, name: 'Combining Latin Small Letter X', symbol: '⟨◌ͯ⟩', category: 'combining' },

  // ===== ARABIC LETTER MARK =====
  { code: 0x061C, name: 'Arabic Letter Mark', symbol: '⟨ALM⟩', category: 'directional' },

  // ===== SYRIAC ABBREVIATION MARK =====
  { code: 0x070F, name: 'Syriac Abbreviation Mark', symbol: '⟨SAM⟩', category: 'control' },

  // ===== HANGUL FILLERS =====
  { code: 0x115F, name: 'Hangul Choseong Filler', symbol: '⟨HCF⟩', category: 'zero-width' },
  { code: 0x1160, name: 'Hangul Jungseong Filler', symbol: '⟨HJF⟩', category: 'zero-width' },

  // ===== ETHIOPIC WORDSPACE =====
  { code: 0x1361, name: 'Ethiopic Wordspace', symbol: '⟨EWS⟩', category: 'space' },

  // ===== KHMER VOWELS =====
  { code: 0x17B4, name: 'Khmer Vowel Inherent Aq', symbol: '⟨KVIA⟩', category: 'zero-width' },
  { code: 0x17B5, name: 'Khmer Vowel Inherent Aa', symbol: '⟨KVAA⟩', category: 'zero-width' },

  // ===== MONGOLIAN CHARACTERS =====
  { code: 0x180B, name: 'Mongolian Free Variation Selector One', symbol: '⟨FVS1⟩', category: 'zero-width' },
  { code: 0x180C, name: 'Mongolian Free Variation Selector Two', symbol: '⟨FVS2⟩', category: 'zero-width' },
  { code: 0x180D, name: 'Mongolian Free Variation Selector Three', symbol: '⟨FVS3⟩', category: 'zero-width' },
  { code: 0x180E, name: 'Mongolian Vowel Separator', symbol: '⟨MVS⟩', category: 'space' },

  // ===== COMBINING DIACRITICAL MARKS EXTENDED (U+1AB0-U+1AFF) - Selected =====
  { code: 0x1AB0, name: 'Combining Doubled Circumflex Accent', symbol: '⟨◌᪰⟩', category: 'combining' },
  { code: 0x1AB1, name: 'Combining Diaeresis-Ring', symbol: '⟨◌᪱⟩', category: 'combining' },
  { code: 0x1AB2, name: 'Combining Infinity', symbol: '⟨◌᪲⟩', category: 'combining' },
  { code: 0x1AB3, name: 'Combining Downwards Arrow', symbol: '⟨◌᪳⟩', category: 'combining' },
  { code: 0x1AB4, name: 'Combining Triple Dot', symbol: '⟨◌᪴⟩', category: 'combining' },
  { code: 0x1AB5, name: 'Combining X-X Below', symbol: '⟨◌᪵⟩', category: 'combining' },
  { code: 0x1AB6, name: 'Combining Wiggly Line Below', symbol: '⟨◌᪶⟩', category: 'combining' },
  { code: 0x1AB7, name: 'Combining Open Mark Below', symbol: '⟨◌᪷⟩', category: 'combining' },
  { code: 0x1AB8, name: 'Combining Double Open Mark Below', symbol: '⟨◌᪸⟩', category: 'combining' },
  { code: 0x1AB9, name: 'Combining Light Centralization Stroke Below', symbol: '⟨◌᪹⟩', category: 'combining' },
  { code: 0x1ABA, name: 'Combining Strong Centralization Stroke Below', symbol: '⟨◌᪺⟩', category: 'combining' },
  { code: 0x1ABB, name: 'Combining Parentheses Above', symbol: '⟨◌᪻⟩', category: 'combining' },
  { code: 0x1ABC, name: 'Combining Double Parentheses Above', symbol: '⟨◌᪼⟩', category: 'combining' },
  { code: 0x1ABD, name: 'Combining Parentheses Below', symbol: '⟨◌᪽⟩', category: 'combining' },
  { code: 0x1ABE, name: 'Combining Parentheses Overlay', symbol: '⟨◌᪾⟩', category: 'combining' },

  // ===== COMBINING DIACRITICAL MARKS SUPPLEMENT (U+1DC0-U+1DFF) - Selected =====
  { code: 0x1DC0, name: 'Combining Dotted Grave Accent', symbol: '⟨◌᷀⟩', category: 'combining' },
  { code: 0x1DC1, name: 'Combining Dotted Acute Accent', symbol: '⟨◌᷁⟩', category: 'combining' },
  { code: 0x1DC2, name: 'Combining Snake Below', symbol: '⟨◌᷂⟩', category: 'combining' },
  { code: 0x1DC3, name: 'Combining Suspension Mark', symbol: '⟨◌᷃⟩', category: 'combining' },
  { code: 0x1DC4, name: 'Combining Macron-Acute', symbol: '⟨◌᷄⟩', category: 'combining' },
  { code: 0x1DC5, name: 'Combining Grave-Macron', symbol: '⟨◌᷅⟩', category: 'combining' },
  { code: 0x1DC6, name: 'Combining Macron-Grave', symbol: '⟨◌᷆⟩', category: 'combining' },
  { code: 0x1DC7, name: 'Combining Acute-Macron', symbol: '⟨◌᷇⟩', category: 'combining' },
  { code: 0x1DC8, name: 'Combining Grave-Acute-Grave', symbol: '⟨◌᷈⟩', category: 'combining' },
  { code: 0x1DC9, name: 'Combining Acute-Grave-Acute', symbol: '⟨◌᷉⟩', category: 'combining' },
  { code: 0x1DCA, name: 'Combining Latin Small Letter R Below', symbol: '⟨◌᷊⟩', category: 'combining' },
  { code: 0x1DCB, name: 'Combining Breve-Macron', symbol: '⟨◌᷋⟩', category: 'combining' },
  { code: 0x1DCC, name: 'Combining Macron-Breve', symbol: '⟨◌᷌⟩', category: 'combining' },
  { code: 0x1DCD, name: 'Combining Double Circumflex Above', symbol: '⟨◌᷍⟩', category: 'combining' },
  { code: 0x1DCE, name: 'Combining Ogonek Above', symbol: '⟨◌᷎⟩', category: 'combining' },
  { code: 0x1DCF, name: 'Combining Zigzag Below', symbol: '⟨◌᷏⟩', category: 'combining' },

  // ===== ZERO-WIDTH CHARACTERS =====
  { code: 0x200B, name: 'Zero-Width Space', symbol: '⟨ZWSP⟩', category: 'zero-width' },
  { code: 0x200C, name: 'Zero-Width Non-Joiner', symbol: '⟨ZWNJ⟩', category: 'zero-width' },
  { code: 0x200D, name: 'Zero-Width Joiner', symbol: '⟨ZWJ⟩', category: 'zero-width' },

  // ===== DIRECTIONAL MARKS =====
  { code: 0x200E, name: 'Left-to-Right Mark', symbol: '⟨LRM⟩', category: 'directional' },
  { code: 0x200F, name: 'Right-to-Left Mark', symbol: '⟨RLM⟩', category: 'directional' },

  // ===== HYPHENS =====
  { code: 0x2010, name: 'Hyphen', symbol: '‐', category: 'control' },
  { code: 0x2011, name: 'Non-Breaking Hyphen', symbol: '‑', category: 'control' },

  // ===== LINE AND PARAGRAPH SEPARATORS =====
  { code: 0x2028, name: 'Line Separator', symbol: '⟨LS⟩', category: 'control' },
  { code: 0x2029, name: 'Paragraph Separator', symbol: '¶', category: 'control' },

  // ===== DIRECTIONAL FORMATTING =====
  { code: 0x202A, name: 'Left-to-Right Embedding', symbol: '⟨LRE⟩', category: 'directional' },
  { code: 0x202B, name: 'Right-to-Left Embedding', symbol: '⟨RLE⟩', category: 'directional' },
  { code: 0x202C, name: 'Pop Directional Formatting', symbol: '⟨PDF⟩', category: 'directional' },
  { code: 0x202D, name: 'Left-to-Right Override', symbol: '⟨LRO⟩', category: 'directional' },
  { code: 0x202E, name: 'Right-to-Left Override', symbol: '⟨RLO⟩', category: 'directional' },

  // ===== ZERO-WIDTH JOINERS AND FUNCTION APPLICATION =====
  { code: 0x2060, name: 'Word Joiner', symbol: '⟨WJ⟩', category: 'zero-width' },
  { code: 0x2061, name: 'Function Application', symbol: '⟨FA⟩', category: 'zero-width' },
  { code: 0x2062, name: 'Invisible Times', symbol: '⟨IT⟩', category: 'zero-width' },
  { code: 0x2063, name: 'Invisible Separator', symbol: '⟨IS⟩', category: 'zero-width' },
  { code: 0x2064, name: 'Invisible Plus', symbol: '⟨IP⟩', category: 'zero-width' },
  { code: 0x2065, name: 'Reserved (Invisible)', symbol: '⟨RSV⟩', category: 'zero-width' },

  // ===== DIRECTIONAL ISOLATES =====
  { code: 0x2066, name: 'Left-to-Right Isolate', symbol: '⟨LRI⟩', category: 'directional' },
  { code: 0x2067, name: 'Right-to-Left Isolate', symbol: '⟨RLI⟩', category: 'directional' },
  { code: 0x2068, name: 'First Strong Isolate', symbol: '⟨FSI⟩', category: 'directional' },
  { code: 0x2069, name: 'Pop Directional Isolate', symbol: '⟨PDI⟩', category: 'directional' },

  // ===== DEPRECATED FORMATTING =====
  { code: 0x206A, name: 'Inhibit Symmetric Swapping', symbol: '⟨ISS⟩', category: 'deprecated' },
  { code: 0x206B, name: 'Activate Symmetric Swapping', symbol: '⟨ASS⟩', category: 'deprecated' },
  { code: 0x206C, name: 'Inhibit Arabic Form Shaping', symbol: '⟨IAFS⟩', category: 'deprecated' },
  { code: 0x206D, name: 'Activate Arabic Form Shaping', symbol: '⟨AAFS⟩', category: 'deprecated' },
  { code: 0x206E, name: 'National Digit Shapes', symbol: '⟨NDS⟩', category: 'deprecated' },
  { code: 0x206F, name: 'Nominal Digit Shapes', symbol: '⟨NODS⟩', category: 'deprecated' },

  // ===== COMBINING MARKS FOR SYMBOLS (U+20D0-U+20FF) - Selected =====
  { code: 0x20D0, name: 'Combining Left Harpoon Above', symbol: '⟨◌⃐⟩', category: 'combining' },
  { code: 0x20D1, name: 'Combining Right Harpoon Above', symbol: '⟨◌⃑⟩', category: 'combining' },
  { code: 0x20D2, name: 'Combining Long Vertical Line Overlay', symbol: '⟨◌⃒⟩', category: 'combining' },
  { code: 0x20D3, name: 'Combining Short Vertical Line Overlay', symbol: '⟨◌⃓⟩', category: 'combining' },
  { code: 0x20D4, name: 'Combining Anticlockwise Arrow Above', symbol: '⟨◌⃔⟩', category: 'combining' },
  { code: 0x20D5, name: 'Combining Clockwise Arrow Above', symbol: '⟨◌⃕⟩', category: 'combining' },
  { code: 0x20D6, name: 'Combining Left Arrow Above', symbol: '⟨◌⃖⟩', category: 'combining' },
  { code: 0x20D7, name: 'Combining Right Arrow Above', symbol: '⟨◌⃗⟩', category: 'combining' },
  { code: 0x20D8, name: 'Combining Ring Overlay', symbol: '⟨◌⃘⟩', category: 'combining' },
  { code: 0x20D9, name: 'Combining Clockwise Ring Overlay', symbol: '⟨◌⃙⟩', category: 'combining' },
  { code: 0x20DA, name: 'Combining Anticlockwise Ring Overlay', symbol: '⟨◌⃚⟩', category: 'combining' },
  { code: 0x20DB, name: 'Combining Three Dots Above', symbol: '⟨◌⃛⟩', category: 'combining' },
  { code: 0x20DC, name: 'Combining Four Dots Above', symbol: '⟨◌⃜⟩', category: 'combining' },
  { code: 0x20DD, name: 'Combining Enclosing Circle', symbol: '⟨◌⃝⟩', category: 'combining' },
  { code: 0x20DE, name: 'Combining Enclosing Square', symbol: '⟨◌⃞⟩', category: 'combining' },
  { code: 0x20DF, name: 'Combining Enclosing Diamond', symbol: '⟨◌⃟⟩', category: 'combining' },
  { code: 0x20E0, name: 'Combining Enclosing Circle Backslash', symbol: '⟨◌⃠⟩', category: 'combining' },
  { code: 0x20E1, name: 'Combining Left Right Arrow Above', symbol: '⟨◌⃡⟩', category: 'combining' },
  { code: 0x20E2, name: 'Combining Enclosing Screen', symbol: '⟨◌⃢⟩', category: 'combining' },
  { code: 0x20E3, name: 'Combining Enclosing Keycap', symbol: '⟨◌⃣⟩', category: 'combining' },
  { code: 0x20E4, name: 'Combining Enclosing Upward Pointing Triangle', symbol: '⟨◌⃤⟩', category: 'combining' },
  { code: 0x20E5, name: 'Combining Reverse Solidus Overlay', symbol: '⟨◌⃥⟩', category: 'combining' },
  { code: 0x20E6, name: 'Combining Double Vertical Stroke Overlay', symbol: '⟨◌⃦⟩', category: 'combining' },
  { code: 0x20E7, name: 'Combining Annuity Symbol', symbol: '⟨◌⃧⟩', category: 'combining' },
  { code: 0x20E8, name: 'Combining Triple Underdot', symbol: '⟨◌⃨⟩', category: 'combining' },
  { code: 0x20E9, name: 'Combining Wide Bridge Above', symbol: '⟨◌⃩⟩', category: 'combining' },
  { code: 0x20EA, name: 'Combining Leftwards Arrow Overlay', symbol: '⟨◌⃪⟩', category: 'combining' },
  { code: 0x20EB, name: 'Combining Long Double Solidus Overlay', symbol: '⟨◌⃫⟩', category: 'combining' },
  { code: 0x20EC, name: 'Combining Rightwards Harpoon with Barb Downwards', symbol: '⟨◌⃬⟩', category: 'combining' },
  { code: 0x20ED, name: 'Combining Leftwards Harpoon with Barb Downwards', symbol: '⟨◌⃭⟩', category: 'combining' },
  { code: 0x20EE, name: 'Combining Left Arrow Below', symbol: '⟨◌⃮⟩', category: 'combining' },
  { code: 0x20EF, name: 'Combining Right Arrow Below', symbol: '⟨◌⃯⟩', category: 'combining' },
  { code: 0x20F0, name: 'Combining Asterisk Above', symbol: '⟨◌⃰⟩', category: 'combining' },

  // ===== BRAILLE PATTERN BLANK =====
  { code: 0x2800, name: 'Braille Pattern Blank', symbol: '⟨⠀⟩', category: 'space' },

  // ===== HANGUL FILLER =====
  { code: 0x3164, name: 'Hangul Filler', symbol: '⟨HF⟩', category: 'zero-width' },

  // ===== VARIATION SELECTORS (U+FE00-U+FE0F) =====
  { code: 0xFE00, name: 'Variation Selector-1', symbol: '⟨VS1⟩', category: 'zero-width' },
  { code: 0xFE01, name: 'Variation Selector-2', symbol: '⟨VS2⟩', category: 'zero-width' },
  { code: 0xFE02, name: 'Variation Selector-3', symbol: '⟨VS3⟩', category: 'zero-width' },
  { code: 0xFE03, name: 'Variation Selector-4', symbol: '⟨VS4⟩', category: 'zero-width' },
  { code: 0xFE04, name: 'Variation Selector-5', symbol: '⟨VS5⟩', category: 'zero-width' },
  { code: 0xFE05, name: 'Variation Selector-6', symbol: '⟨VS6⟩', category: 'zero-width' },
  { code: 0xFE06, name: 'Variation Selector-7', symbol: '⟨VS7⟩', category: 'zero-width' },
  { code: 0xFE07, name: 'Variation Selector-8', symbol: '⟨VS8⟩', category: 'zero-width' },
  { code: 0xFE08, name: 'Variation Selector-9', symbol: '⟨VS9⟩', category: 'zero-width' },
  { code: 0xFE09, name: 'Variation Selector-10', symbol: '⟨VS10⟩', category: 'zero-width' },
  { code: 0xFE0A, name: 'Variation Selector-11', symbol: '⟨VS11⟩', category: 'zero-width' },
  { code: 0xFE0B, name: 'Variation Selector-12', symbol: '⟨VS12⟩', category: 'zero-width' },
  { code: 0xFE0C, name: 'Variation Selector-13', symbol: '⟨VS13⟩', category: 'zero-width' },
  { code: 0xFE0D, name: 'Variation Selector-14', symbol: '⟨VS14⟩', category: 'zero-width' },
  { code: 0xFE0E, name: 'Variation Selector-15', symbol: '⟨VS15⟩', category: 'zero-width' },
  { code: 0xFE0F, name: 'Variation Selector-16', symbol: '⟨VS16⟩', category: 'zero-width' },

  // ===== VARIATION SELECTOR SUPPLEMENT (U+E0100-U+E01EF) - 240 characters =====
  // Used for high-density steganography and advanced AI watermarking (8 bits = 256 states)
  { code: 0xE0100, name: 'Variation Selector-17', symbol: '⟨VS17⟩', category: 'zero-width' },
  { code: 0xE0101, name: 'Variation Selector-18', symbol: '⟨VS18⟩', category: 'zero-width' },
  { code: 0xE0102, name: 'Variation Selector-19', symbol: '⟨VS19⟩', category: 'zero-width' },
  { code: 0xE0103, name: 'Variation Selector-20', symbol: '⟨VS20⟩', category: 'zero-width' },
  { code: 0xE0104, name: 'Variation Selector-21', symbol: '⟨VS21⟩', category: 'zero-width' },
  { code: 0xE0105, name: 'Variation Selector-22', symbol: '⟨VS22⟩', category: 'zero-width' },
  { code: 0xE0106, name: 'Variation Selector-23', symbol: '⟨VS23⟩', category: 'zero-width' },
  { code: 0xE0107, name: 'Variation Selector-24', symbol: '⟨VS24⟩', category: 'zero-width' },
  { code: 0xE0108, name: 'Variation Selector-25', symbol: '⟨VS25⟩', category: 'zero-width' },
  { code: 0xE0109, name: 'Variation Selector-26', symbol: '⟨VS26⟩', category: 'zero-width' },
  { code: 0xE010A, name: 'Variation Selector-27', symbol: '⟨VS27⟩', category: 'zero-width' },
  { code: 0xE010B, name: 'Variation Selector-28', symbol: '⟨VS28⟩', category: 'zero-width' },
  { code: 0xE010C, name: 'Variation Selector-29', symbol: '⟨VS29⟩', category: 'zero-width' },
  { code: 0xE010D, name: 'Variation Selector-30', symbol: '⟨VS30⟩', category: 'zero-width' },
  { code: 0xE010E, name: 'Variation Selector-31', symbol: '⟨VS31⟩', category: 'zero-width' },
  { code: 0xE010F, name: 'Variation Selector-32', symbol: '⟨VS32⟩', category: 'zero-width' },
  { code: 0xE0110, name: 'Variation Selector-33', symbol: '⟨VS33⟩', category: 'zero-width' },
  { code: 0xE0111, name: 'Variation Selector-34', symbol: '⟨VS34⟩', category: 'zero-width' },
  { code: 0xE0112, name: 'Variation Selector-35', symbol: '⟨VS35⟩', category: 'zero-width' },
  { code: 0xE0113, name: 'Variation Selector-36', symbol: '⟨VS36⟩', category: 'zero-width' },
  { code: 0xE0114, name: 'Variation Selector-37', symbol: '⟨VS37⟩', category: 'zero-width' },
  { code: 0xE0115, name: 'Variation Selector-38', symbol: '⟨VS38⟩', category: 'zero-width' },
  { code: 0xE0116, name: 'Variation Selector-39', symbol: '⟨VS39⟩', category: 'zero-width' },
  { code: 0xE0117, name: 'Variation Selector-40', symbol: '⟨VS40⟩', category: 'zero-width' },
  { code: 0xE0118, name: 'Variation Selector-41', symbol: '⟨VS41⟩', category: 'zero-width' },
  { code: 0xE0119, name: 'Variation Selector-42', symbol: '⟨VS42⟩', category: 'zero-width' },
  { code: 0xE011A, name: 'Variation Selector-43', symbol: '⟨VS43⟩', category: 'zero-width' },
  { code: 0xE011B, name: 'Variation Selector-44', symbol: '⟨VS44⟩', category: 'zero-width' },
  { code: 0xE011C, name: 'Variation Selector-45', symbol: '⟨VS45⟩', category: 'zero-width' },
  { code: 0xE011D, name: 'Variation Selector-46', symbol: '⟨VS46⟩', category: 'zero-width' },
  { code: 0xE011E, name: 'Variation Selector-47', symbol: '⟨VS47⟩', category: 'zero-width' },
  { code: 0xE011F, name: 'Variation Selector-48', symbol: '⟨VS48⟩', category: 'zero-width' },
  { code: 0xE0120, name: 'Variation Selector-49', symbol: '⟨VS49⟩', category: 'zero-width' },
  { code: 0xE0121, name: 'Variation Selector-50', symbol: '⟨VS50⟩', category: 'zero-width' },
  { code: 0xE0122, name: 'Variation Selector-51', symbol: '⟨VS51⟩', category: 'zero-width' },
  { code: 0xE0123, name: 'Variation Selector-52', symbol: '⟨VS52⟩', category: 'zero-width' },
  { code: 0xE0124, name: 'Variation Selector-53', symbol: '⟨VS53⟩', category: 'zero-width' },
  { code: 0xE0125, name: 'Variation Selector-54', symbol: '⟨VS54⟩', category: 'zero-width' },
  { code: 0xE0126, name: 'Variation Selector-55', symbol: '⟨VS55⟩', category: 'zero-width' },
  { code: 0xE0127, name: 'Variation Selector-56', symbol: '⟨VS56⟩', category: 'zero-width' },
  { code: 0xE0128, name: 'Variation Selector-57', symbol: '⟨VS57⟩', category: 'zero-width' },
  { code: 0xE0129, name: 'Variation Selector-58', symbol: '⟨VS58⟩', category: 'zero-width' },
  { code: 0xE012A, name: 'Variation Selector-59', symbol: '⟨VS59⟩', category: 'zero-width' },
  { code: 0xE012B, name: 'Variation Selector-60', symbol: '⟨VS60⟩', category: 'zero-width' },
  { code: 0xE012C, name: 'Variation Selector-61', symbol: '⟨VS61⟩', category: 'zero-width' },
  { code: 0xE012D, name: 'Variation Selector-62', symbol: '⟨VS62⟩', category: 'zero-width' },
  { code: 0xE012E, name: 'Variation Selector-63', symbol: '⟨VS63⟩', category: 'zero-width' },
  { code: 0xE012F, name: 'Variation Selector-64', symbol: '⟨VS64⟩', category: 'zero-width' },
  { code: 0xE0130, name: 'Variation Selector-65', symbol: '⟨VS65⟩', category: 'zero-width' },
  { code: 0xE0131, name: 'Variation Selector-66', symbol: '⟨VS66⟩', category: 'zero-width' },
  { code: 0xE0132, name: 'Variation Selector-67', symbol: '⟨VS67⟩', category: 'zero-width' },
  { code: 0xE0133, name: 'Variation Selector-68', symbol: '⟨VS68⟩', category: 'zero-width' },
  { code: 0xE0134, name: 'Variation Selector-69', symbol: '⟨VS69⟩', category: 'zero-width' },
  { code: 0xE0135, name: 'Variation Selector-70', symbol: '⟨VS70⟩', category: 'zero-width' },
  { code: 0xE0136, name: 'Variation Selector-71', symbol: '⟨VS71⟩', category: 'zero-width' },
  { code: 0xE0137, name: 'Variation Selector-72', symbol: '⟨VS72⟩', category: 'zero-width' },
  { code: 0xE0138, name: 'Variation Selector-73', symbol: '⟨VS73⟩', category: 'zero-width' },
  { code: 0xE0139, name: 'Variation Selector-74', symbol: '⟨VS74⟩', category: 'zero-width' },
  { code: 0xE013A, name: 'Variation Selector-75', symbol: '⟨VS75⟩', category: 'zero-width' },
  { code: 0xE013B, name: 'Variation Selector-76', symbol: '⟨VS76⟩', category: 'zero-width' },
  { code: 0xE013C, name: 'Variation Selector-77', symbol: '⟨VS77⟩', category: 'zero-width' },
  { code: 0xE013D, name: 'Variation Selector-78', symbol: '⟨VS78⟩', category: 'zero-width' },
  { code: 0xE013E, name: 'Variation Selector-79', symbol: '⟨VS79⟩', category: 'zero-width' },
  { code: 0xE013F, name: 'Variation Selector-80', symbol: '⟨VS80⟩', category: 'zero-width' },
  { code: 0xE0140, name: 'Variation Selector-81', symbol: '⟨VS81⟩', category: 'zero-width' },
  { code: 0xE0141, name: 'Variation Selector-82', symbol: '⟨VS82⟩', category: 'zero-width' },
  { code: 0xE0142, name: 'Variation Selector-83', symbol: '⟨VS83⟩', category: 'zero-width' },
  { code: 0xE0143, name: 'Variation Selector-84', symbol: '⟨VS84⟩', category: 'zero-width' },
  { code: 0xE0144, name: 'Variation Selector-85', symbol: '⟨VS85⟩', category: 'zero-width' },
  { code: 0xE0145, name: 'Variation Selector-86', symbol: '⟨VS86⟩', category: 'zero-width' },
  { code: 0xE0146, name: 'Variation Selector-87', symbol: '⟨VS87⟩', category: 'zero-width' },
  { code: 0xE0147, name: 'Variation Selector-88', symbol: '⟨VS88⟩', category: 'zero-width' },
  { code: 0xE0148, name: 'Variation Selector-89', symbol: '⟨VS89⟩', category: 'zero-width' },
  { code: 0xE0149, name: 'Variation Selector-90', symbol: '⟨VS90⟩', category: 'zero-width' },
  { code: 0xE014A, name: 'Variation Selector-91', symbol: '⟨VS91⟩', category: 'zero-width' },
  { code: 0xE014B, name: 'Variation Selector-92', symbol: '⟨VS92⟩', category: 'zero-width' },
  { code: 0xE014C, name: 'Variation Selector-93', symbol: '⟨VS93⟩', category: 'zero-width' },
  { code: 0xE014D, name: 'Variation Selector-94', symbol: '⟨VS94⟩', category: 'zero-width' },
  { code: 0xE014E, name: 'Variation Selector-95', symbol: '⟨VS95⟩', category: 'zero-width' },
  { code: 0xE014F, name: 'Variation Selector-96', symbol: '⟨VS96⟩', category: 'zero-width' },
  { code: 0xE0150, name: 'Variation Selector-97', symbol: '⟨VS97⟩', category: 'zero-width' },
  { code: 0xE0151, name: 'Variation Selector-98', symbol: '⟨VS98⟩', category: 'zero-width' },
  { code: 0xE0152, name: 'Variation Selector-99', symbol: '⟨VS99⟩', category: 'zero-width' },
  { code: 0xE0153, name: 'Variation Selector-100', symbol: '⟨VS100⟩', category: 'zero-width' },
  { code: 0xE0154, name: 'Variation Selector-101', symbol: '⟨VS101⟩', category: 'zero-width' },
  { code: 0xE0155, name: 'Variation Selector-102', symbol: '⟨VS102⟩', category: 'zero-width' },
  { code: 0xE0156, name: 'Variation Selector-103', symbol: '⟨VS103⟩', category: 'zero-width' },
  { code: 0xE0157, name: 'Variation Selector-104', symbol: '⟨VS104⟩', category: 'zero-width' },
  { code: 0xE0158, name: 'Variation Selector-105', symbol: '⟨VS105⟩', category: 'zero-width' },
  { code: 0xE0159, name: 'Variation Selector-106', symbol: '⟨VS106⟩', category: 'zero-width' },
  { code: 0xE015A, name: 'Variation Selector-107', symbol: '⟨VS107⟩', category: 'zero-width' },
  { code: 0xE015B, name: 'Variation Selector-108', symbol: '⟨VS108⟩', category: 'zero-width' },
  { code: 0xE015C, name: 'Variation Selector-109', symbol: '⟨VS109⟩', category: 'zero-width' },
  { code: 0xE015D, name: 'Variation Selector-110', symbol: '⟨VS110⟩', category: 'zero-width' },
  { code: 0xE015E, name: 'Variation Selector-111', symbol: '⟨VS111⟩', category: 'zero-width' },
  { code: 0xE015F, name: 'Variation Selector-112', symbol: '⟨VS112⟩', category: 'zero-width' },
  { code: 0xE0160, name: 'Variation Selector-113', symbol: '⟨VS113⟩', category: 'zero-width' },
  { code: 0xE0161, name: 'Variation Selector-114', symbol: '⟨VS114⟩', category: 'zero-width' },
  { code: 0xE0162, name: 'Variation Selector-115', symbol: '⟨VS115⟩', category: 'zero-width' },
  { code: 0xE0163, name: 'Variation Selector-116', symbol: '⟨VS116⟩', category: 'zero-width' },
  { code: 0xE0164, name: 'Variation Selector-117', symbol: '⟨VS117⟩', category: 'zero-width' },
  { code: 0xE0165, name: 'Variation Selector-118', symbol: '⟨VS118⟩', category: 'zero-width' },
  { code: 0xE0166, name: 'Variation Selector-119', symbol: '⟨VS119⟩', category: 'zero-width' },
  { code: 0xE0167, name: 'Variation Selector-120', symbol: '⟨VS120⟩', category: 'zero-width' },
  { code: 0xE0168, name: 'Variation Selector-121', symbol: '⟨VS121⟩', category: 'zero-width' },
  { code: 0xE0169, name: 'Variation Selector-122', symbol: '⟨VS122⟩', category: 'zero-width' },
  { code: 0xE016A, name: 'Variation Selector-123', symbol: '⟨VS123⟩', category: 'zero-width' },
  { code: 0xE016B, name: 'Variation Selector-124', symbol: '⟨VS124⟩', category: 'zero-width' },
  { code: 0xE016C, name: 'Variation Selector-125', symbol: '⟨VS125⟩', category: 'zero-width' },
  { code: 0xE016D, name: 'Variation Selector-126', symbol: '⟨VS126⟩', category: 'zero-width' },
  { code: 0xE016E, name: 'Variation Selector-127', symbol: '⟨VS127⟩', category: 'zero-width' },
  { code: 0xE016F, name: 'Variation Selector-128', symbol: '⟨VS128⟩', category: 'zero-width' },
  { code: 0xE0170, name: 'Variation Selector-129', symbol: '⟨VS129⟩', category: 'zero-width' },
  { code: 0xE0171, name: 'Variation Selector-130', symbol: '⟨VS130⟩', category: 'zero-width' },
  { code: 0xE0172, name: 'Variation Selector-131', symbol: '⟨VS131⟩', category: 'zero-width' },
  { code: 0xE0173, name: 'Variation Selector-132', symbol: '⟨VS132⟩', category: 'zero-width' },
  { code: 0xE0174, name: 'Variation Selector-133', symbol: '⟨VS133⟩', category: 'zero-width' },
  { code: 0xE0175, name: 'Variation Selector-134', symbol: '⟨VS134⟩', category: 'zero-width' },
  { code: 0xE0176, name: 'Variation Selector-135', symbol: '⟨VS135⟩', category: 'zero-width' },
  { code: 0xE0177, name: 'Variation Selector-136', symbol: '⟨VS136⟩', category: 'zero-width' },
  { code: 0xE0178, name: 'Variation Selector-137', symbol: '⟨VS137⟩', category: 'zero-width' },
  { code: 0xE0179, name: 'Variation Selector-138', symbol: '⟨VS138⟩', category: 'zero-width' },
  { code: 0xE017A, name: 'Variation Selector-139', symbol: '⟨VS139⟩', category: 'zero-width' },
  { code: 0xE017B, name: 'Variation Selector-140', symbol: '⟨VS140⟩', category: 'zero-width' },
  { code: 0xE017C, name: 'Variation Selector-141', symbol: '⟨VS141⟩', category: 'zero-width' },
  { code: 0xE017D, name: 'Variation Selector-142', symbol: '⟨VS142⟩', category: 'zero-width' },
  { code: 0xE017E, name: 'Variation Selector-143', symbol: '⟨VS143⟩', category: 'zero-width' },
  { code: 0xE017F, name: 'Variation Selector-144', symbol: '⟨VS144⟩', category: 'zero-width' },
  { code: 0xE0180, name: 'Variation Selector-145', symbol: '⟨VS145⟩', category: 'zero-width' },
  { code: 0xE0181, name: 'Variation Selector-146', symbol: '⟨VS146⟩', category: 'zero-width' },
  { code: 0xE0182, name: 'Variation Selector-147', symbol: '⟨VS147⟩', category: 'zero-width' },
  { code: 0xE0183, name: 'Variation Selector-148', symbol: '⟨VS148⟩', category: 'zero-width' },
  { code: 0xE0184, name: 'Variation Selector-149', symbol: '⟨VS149⟩', category: 'zero-width' },
  { code: 0xE0185, name: 'Variation Selector-150', symbol: '⟨VS150⟩', category: 'zero-width' },
  { code: 0xE0186, name: 'Variation Selector-151', symbol: '⟨VS151⟩', category: 'zero-width' },
  { code: 0xE0187, name: 'Variation Selector-152', symbol: '⟨VS152⟩', category: 'zero-width' },
  { code: 0xE0188, name: 'Variation Selector-153', symbol: '⟨VS153⟩', category: 'zero-width' },
  { code: 0xE0189, name: 'Variation Selector-154', symbol: '⟨VS154⟩', category: 'zero-width' },
  { code: 0xE018A, name: 'Variation Selector-155', symbol: '⟨VS155⟩', category: 'zero-width' },
  { code: 0xE018B, name: 'Variation Selector-156', symbol: '⟨VS156⟩', category: 'zero-width' },
  { code: 0xE018C, name: 'Variation Selector-157', symbol: '⟨VS157⟩', category: 'zero-width' },
  { code: 0xE018D, name: 'Variation Selector-158', symbol: '⟨VS158⟩', category: 'zero-width' },
  { code: 0xE018E, name: 'Variation Selector-159', symbol: '⟨VS159⟩', category: 'zero-width' },
  { code: 0xE018F, name: 'Variation Selector-160', symbol: '⟨VS160⟩', category: 'zero-width' },
  { code: 0xE0190, name: 'Variation Selector-161', symbol: '⟨VS161⟩', category: 'zero-width' },
  { code: 0xE0191, name: 'Variation Selector-162', symbol: '⟨VS162⟩', category: 'zero-width' },
  { code: 0xE0192, name: 'Variation Selector-163', symbol: '⟨VS163⟩', category: 'zero-width' },
  { code: 0xE0193, name: 'Variation Selector-164', symbol: '⟨VS164⟩', category: 'zero-width' },
  { code: 0xE0194, name: 'Variation Selector-165', symbol: '⟨VS165⟩', category: 'zero-width' },
  { code: 0xE0195, name: 'Variation Selector-166', symbol: '⟨VS166⟩', category: 'zero-width' },
  { code: 0xE0196, name: 'Variation Selector-167', symbol: '⟨VS167⟩', category: 'zero-width' },
  { code: 0xE0197, name: 'Variation Selector-168', symbol: '⟨VS168⟩', category: 'zero-width' },
  { code: 0xE0198, name: 'Variation Selector-169', symbol: '⟨VS169⟩', category: 'zero-width' },
  { code: 0xE0199, name: 'Variation Selector-170', symbol: '⟨VS170⟩', category: 'zero-width' },
  { code: 0xE019A, name: 'Variation Selector-171', symbol: '⟨VS171⟩', category: 'zero-width' },
  { code: 0xE019B, name: 'Variation Selector-172', symbol: '⟨VS172⟩', category: 'zero-width' },
  { code: 0xE019C, name: 'Variation Selector-173', symbol: '⟨VS173⟩', category: 'zero-width' },
  { code: 0xE019D, name: 'Variation Selector-174', symbol: '⟨VS174⟩', category: 'zero-width' },
  { code: 0xE019E, name: 'Variation Selector-175', symbol: '⟨VS175⟩', category: 'zero-width' },
  { code: 0xE019F, name: 'Variation Selector-176', symbol: '⟨VS176⟩', category: 'zero-width' },
  { code: 0xE01A0, name: 'Variation Selector-177', symbol: '⟨VS177⟩', category: 'zero-width' },
  { code: 0xE01A1, name: 'Variation Selector-178', symbol: '⟨VS178⟩', category: 'zero-width' },
  { code: 0xE01A2, name: 'Variation Selector-179', symbol: '⟨VS179⟩', category: 'zero-width' },
  { code: 0xE01A3, name: 'Variation Selector-180', symbol: '⟨VS180⟩', category: 'zero-width' },
  { code: 0xE01A4, name: 'Variation Selector-181', symbol: '⟨VS181⟩', category: 'zero-width' },
  { code: 0xE01A5, name: 'Variation Selector-182', symbol: '⟨VS182⟩', category: 'zero-width' },
  { code: 0xE01A6, name: 'Variation Selector-183', symbol: '⟨VS183⟩', category: 'zero-width' },
  { code: 0xE01A7, name: 'Variation Selector-184', symbol: '⟨VS184⟩', category: 'zero-width' },
  { code: 0xE01A8, name: 'Variation Selector-185', symbol: '⟨VS185⟩', category: 'zero-width' },
  { code: 0xE01A9, name: 'Variation Selector-186', symbol: '⟨VS186⟩', category: 'zero-width' },
  { code: 0xE01AA, name: 'Variation Selector-187', symbol: '⟨VS187⟩', category: 'zero-width' },
  { code: 0xE01AB, name: 'Variation Selector-188', symbol: '⟨VS188⟩', category: 'zero-width' },
  { code: 0xE01AC, name: 'Variation Selector-189', symbol: '⟨VS189⟩', category: 'zero-width' },
  { code: 0xE01AD, name: 'Variation Selector-190', symbol: '⟨VS190⟩', category: 'zero-width' },
  { code: 0xE01AE, name: 'Variation Selector-191', symbol: '⟨VS191⟩', category: 'zero-width' },
  { code: 0xE01AF, name: 'Variation Selector-192', symbol: '⟨VS192⟩', category: 'zero-width' },
  { code: 0xE01B0, name: 'Variation Selector-193', symbol: '⟨VS193⟩', category: 'zero-width' },
  { code: 0xE01B1, name: 'Variation Selector-194', symbol: '⟨VS194⟩', category: 'zero-width' },
  { code: 0xE01B2, name: 'Variation Selector-195', symbol: '⟨VS195⟩', category: 'zero-width' },
  { code: 0xE01B3, name: 'Variation Selector-196', symbol: '⟨VS196⟩', category: 'zero-width' },
  { code: 0xE01B4, name: 'Variation Selector-197', symbol: '⟨VS197⟩', category: 'zero-width' },
  { code: 0xE01B5, name: 'Variation Selector-198', symbol: '⟨VS198⟩', category: 'zero-width' },
  { code: 0xE01B6, name: 'Variation Selector-199', symbol: '⟨VS199⟩', category: 'zero-width' },
  { code: 0xE01B7, name: 'Variation Selector-200', symbol: '⟨VS200⟩', category: 'zero-width' },
  { code: 0xE01B8, name: 'Variation Selector-201', symbol: '⟨VS201⟩', category: 'zero-width' },
  { code: 0xE01B9, name: 'Variation Selector-202', symbol: '⟨VS202⟩', category: 'zero-width' },
  { code: 0xE01BA, name: 'Variation Selector-203', symbol: '⟨VS203⟩', category: 'zero-width' },
  { code: 0xE01BB, name: 'Variation Selector-204', symbol: '⟨VS204⟩', category: 'zero-width' },
  { code: 0xE01BC, name: 'Variation Selector-205', symbol: '⟨VS205⟩', category: 'zero-width' },
  { code: 0xE01BD, name: 'Variation Selector-206', symbol: '⟨VS206⟩', category: 'zero-width' },
  { code: 0xE01BE, name: 'Variation Selector-207', symbol: '⟨VS207⟩', category: 'zero-width' },
  { code: 0xE01BF, name: 'Variation Selector-208', symbol: '⟨VS208⟩', category: 'zero-width' },
  { code: 0xE01C0, name: 'Variation Selector-209', symbol: '⟨VS209⟩', category: 'zero-width' },
  { code: 0xE01C1, name: 'Variation Selector-210', symbol: '⟨VS210⟩', category: 'zero-width' },
  { code: 0xE01C2, name: 'Variation Selector-211', symbol: '⟨VS211⟩', category: 'zero-width' },
  { code: 0xE01C3, name: 'Variation Selector-212', symbol: '⟨VS212⟩', category: 'zero-width' },
  { code: 0xE01C4, name: 'Variation Selector-213', symbol: '⟨VS213⟩', category: 'zero-width' },
  { code: 0xE01C5, name: 'Variation Selector-214', symbol: '⟨VS214⟩', category: 'zero-width' },
  { code: 0xE01C6, name: 'Variation Selector-215', symbol: '⟨VS215⟩', category: 'zero-width' },
  { code: 0xE01C7, name: 'Variation Selector-216', symbol: '⟨VS216⟩', category: 'zero-width' },
  { code: 0xE01C8, name: 'Variation Selector-217', symbol: '⟨VS217⟩', category: 'zero-width' },
  { code: 0xE01C9, name: 'Variation Selector-218', symbol: '⟨VS218⟩', category: 'zero-width' },
  { code: 0xE01CA, name: 'Variation Selector-219', symbol: '⟨VS219⟩', category: 'zero-width' },
  { code: 0xE01CB, name: 'Variation Selector-220', symbol: '⟨VS220⟩', category: 'zero-width' },
  { code: 0xE01CC, name: 'Variation Selector-221', symbol: '⟨VS221⟩', category: 'zero-width' },
  { code: 0xE01CD, name: 'Variation Selector-222', symbol: '⟨VS222⟩', category: 'zero-width' },
  { code: 0xE01CE, name: 'Variation Selector-223', symbol: '⟨VS223⟩', category: 'zero-width' },
  { code: 0xE01CF, name: 'Variation Selector-224', symbol: '⟨VS224⟩', category: 'zero-width' },
  { code: 0xE01D0, name: 'Variation Selector-225', symbol: '⟨VS225⟩', category: 'zero-width' },
  { code: 0xE01D1, name: 'Variation Selector-226', symbol: '⟨VS226⟩', category: 'zero-width' },
  { code: 0xE01D2, name: 'Variation Selector-227', symbol: '⟨VS227⟩', category: 'zero-width' },
  { code: 0xE01D3, name: 'Variation Selector-228', symbol: '⟨VS228⟩', category: 'zero-width' },
  { code: 0xE01D4, name: 'Variation Selector-229', symbol: '⟨VS229⟩', category: 'zero-width' },
  { code: 0xE01D5, name: 'Variation Selector-230', symbol: '⟨VS230⟩', category: 'zero-width' },
  { code: 0xE01D6, name: 'Variation Selector-231', symbol: '⟨VS231⟩', category: 'zero-width' },
  { code: 0xE01D7, name: 'Variation Selector-232', symbol: '⟨VS232⟩', category: 'zero-width' },
  { code: 0xE01D8, name: 'Variation Selector-233', symbol: '⟨VS233⟩', category: 'zero-width' },
  { code: 0xE01D9, name: 'Variation Selector-234', symbol: '⟨VS234⟩', category: 'zero-width' },
  { code: 0xE01DA, name: 'Variation Selector-235', symbol: '⟨VS235⟩', category: 'zero-width' },
  { code: 0xE01DB, name: 'Variation Selector-236', symbol: '⟨VS236⟩', category: 'zero-width' },
  { code: 0xE01DC, name: 'Variation Selector-237', symbol: '⟨VS237⟩', category: 'zero-width' },
  { code: 0xE01DD, name: 'Variation Selector-238', symbol: '⟨VS238⟩', category: 'zero-width' },
  { code: 0xE01DE, name: 'Variation Selector-239', symbol: '⟨VS239⟩', category: 'zero-width' },
  { code: 0xE01DF, name: 'Variation Selector-240', symbol: '⟨VS240⟩', category: 'zero-width' },
  { code: 0xE01E0, name: 'Variation Selector-241', symbol: '⟨VS241⟩', category: 'zero-width' },
  { code: 0xE01E1, name: 'Variation Selector-242', symbol: '⟨VS242⟩', category: 'zero-width' },
  { code: 0xE01E2, name: 'Variation Selector-243', symbol: '⟨VS243⟩', category: 'zero-width' },
  { code: 0xE01E3, name: 'Variation Selector-244', symbol: '⟨VS244⟩', category: 'zero-width' },
  { code: 0xE01E4, name: 'Variation Selector-245', symbol: '⟨VS245⟩', category: 'zero-width' },
  { code: 0xE01E5, name: 'Variation Selector-246', symbol: '⟨VS246⟩', category: 'zero-width' },
  { code: 0xE01E6, name: 'Variation Selector-247', symbol: '⟨VS247⟩', category: 'zero-width' },
  { code: 0xE01E7, name: 'Variation Selector-248', symbol: '⟨VS248⟩', category: 'zero-width' },
  { code: 0xE01E8, name: 'Variation Selector-249', symbol: '⟨VS249⟩', category: 'zero-width' },
  { code: 0xE01E9, name: 'Variation Selector-250', symbol: '⟨VS250⟩', category: 'zero-width' },
  { code: 0xE01EA, name: 'Variation Selector-251', symbol: '⟨VS251⟩', category: 'zero-width' },
  { code: 0xE01EB, name: 'Variation Selector-252', symbol: '⟨VS252⟩', category: 'zero-width' },
  { code: 0xE01EC, name: 'Variation Selector-253', symbol: '⟨VS253⟩', category: 'zero-width' },
  { code: 0xE01ED, name: 'Variation Selector-254', symbol: '⟨VS254⟩', category: 'zero-width' },
  { code: 0xE01EE, name: 'Variation Selector-255', symbol: '⟨VS255⟩', category: 'zero-width' },
  { code: 0xE01EF, name: 'Variation Selector-256', symbol: '⟨VS256⟩', category: 'zero-width' },

  // ===== BYTE ORDER MARK =====
  { code: 0xFEFF, name: 'Zero Width No-Break Space (BOM)', symbol: '⟨BOM⟩', category: 'zero-width' },

  // ===== HALFWIDTH HANGUL FILLER =====
  { code: 0xFFA0, name: 'Halfwidth Hangul Filler', symbol: '⟨HHF⟩', category: 'space' },

  // ===== INTERLINEAR ANNOTATION =====
  { code: 0xFFF9, name: 'Interlinear Annotation Anchor', symbol: '⟨IAA⟩', category: 'control' },
  { code: 0xFFFA, name: 'Interlinear Annotation Separator', symbol: '⟨IAS⟩', category: 'control' },
  { code: 0xFFFB, name: 'Interlinear Annotation Terminator', symbol: '⟨IAT⟩', category: 'control' },

  // ===== OBJECT REPLACEMENT CHARACTER =====
  { code: 0xFFFC, name: 'Object Replacement Character', symbol: '⟨OBJ⟩', category: 'control' },

  // ===== TAG CHARACTERS (U+E0000-U+E007F) - Used for language tagging and steganography =====
  { code: 0xE0000, name: 'Tag Code Point', symbol: '⟨T0⟩', category: 'deprecated' },
  { code: 0xE0001, name: 'Language Tag', symbol: '⟨TAG⟩', category: 'deprecated' },
  { code: 0xE0002, name: 'Tag Character U+0002', symbol: '⟨T2⟩', category: 'deprecated' },
  { code: 0xE0003, name: 'Tag Character U+0003', symbol: '⟨T3⟩', category: 'deprecated' },
  { code: 0xE0004, name: 'Tag Character U+0004', symbol: '⟨T4⟩', category: 'deprecated' },
  { code: 0xE0005, name: 'Tag Character U+0005', symbol: '⟨T5⟩', category: 'deprecated' },
  { code: 0xE0006, name: 'Tag Character U+0006', symbol: '⟨T6⟩', category: 'deprecated' },
  { code: 0xE0007, name: 'Tag Character U+0007', symbol: '⟨T7⟩', category: 'deprecated' },
  { code: 0xE0008, name: 'Tag Character U+0008', symbol: '⟨T8⟩', category: 'deprecated' },
  { code: 0xE0009, name: 'Tag Character U+0009', symbol: '⟨T9⟩', category: 'deprecated' },
  { code: 0xE000A, name: 'Tag Character U+000A', symbol: '⟨TA⟩', category: 'deprecated' },
  { code: 0xE000B, name: 'Tag Character U+000B', symbol: '⟨TB⟩', category: 'deprecated' },
  { code: 0xE000C, name: 'Tag Character U+000C', symbol: '⟨TC⟩', category: 'deprecated' },
  { code: 0xE000D, name: 'Tag Character U+000D', symbol: '⟨TD⟩', category: 'deprecated' },
  { code: 0xE000E, name: 'Tag Character U+000E', symbol: '⟨TE⟩', category: 'deprecated' },
  { code: 0xE000F, name: 'Tag Character U+000F', symbol: '⟨TF⟩', category: 'deprecated' },
  { code: 0xE0010, name: 'Tag Character U+0010', symbol: '⟨T10⟩', category: 'deprecated' },
  { code: 0xE0011, name: 'Tag Character U+0011', symbol: '⟨T11⟩', category: 'deprecated' },
  { code: 0xE0012, name: 'Tag Character U+0012', symbol: '⟨T12⟩', category: 'deprecated' },
  { code: 0xE0013, name: 'Tag Character U+0013', symbol: '⟨T13⟩', category: 'deprecated' },
  { code: 0xE0014, name: 'Tag Character U+0014', symbol: '⟨T14⟩', category: 'deprecated' },
  { code: 0xE0015, name: 'Tag Character U+0015', symbol: '⟨T15⟩', category: 'deprecated' },
  { code: 0xE0016, name: 'Tag Character U+0016', symbol: '⟨T16⟩', category: 'deprecated' },
  { code: 0xE0017, name: 'Tag Character U+0017', symbol: '⟨T17⟩', category: 'deprecated' },
  { code: 0xE0018, name: 'Tag Character U+0018', symbol: '⟨T18⟩', category: 'deprecated' },
  { code: 0xE0019, name: 'Tag Character U+0019', symbol: '⟨T19⟩', category: 'deprecated' },
  { code: 0xE001A, name: 'Tag Character U+001A', symbol: '⟨T1A⟩', category: 'deprecated' },
  { code: 0xE001B, name: 'Tag Character U+001B', symbol: '⟨T1B⟩', category: 'deprecated' },
  { code: 0xE001C, name: 'Tag Character U+001C', symbol: '⟨T1C⟩', category: 'deprecated' },
  { code: 0xE001D, name: 'Tag Character U+001D', symbol: '⟨T1D⟩', category: 'deprecated' },
  { code: 0xE001E, name: 'Tag Character U+001E', symbol: '⟨T1E⟩', category: 'deprecated' },
  { code: 0xE001F, name: 'Tag Character U+001F', symbol: '⟨T1F⟩', category: 'deprecated' },
  { code: 0xE0020, name: 'Tag Space', symbol: '⟨T_SP⟩', category: 'deprecated' },
  { code: 0xE0021, name: 'Tag Exclamation Mark', symbol: '⟨T_!⟩', category: 'deprecated' },
  { code: 0xE0022, name: 'Tag Quotation Mark', symbol: '⟨T_"⟩', category: 'deprecated' },
  { code: 0xE0023, name: 'Tag Number Sign', symbol: '⟨T_#⟩', category: 'deprecated' },
  { code: 0xE0024, name: 'Tag Dollar Sign', symbol: '⟨T_$⟩', category: 'deprecated' },
  { code: 0xE0025, name: 'Tag Percent Sign', symbol: '⟨T_%⟩', category: 'deprecated' },
  { code: 0xE0026, name: 'Tag Ampersand', symbol: '⟨T_&⟩', category: 'deprecated' },
  { code: 0xE0027, name: 'Tag Apostrophe', symbol: "⟨T_'⟩", category: 'deprecated' },
  { code: 0xE0028, name: 'Tag Left Parenthesis', symbol: '⟨T_(⟩', category: 'deprecated' },
  { code: 0xE0029, name: 'Tag Right Parenthesis', symbol: '⟨T_)⟩', category: 'deprecated' },
  { code: 0xE002A, name: 'Tag Asterisk', symbol: '⟨T_*⟩', category: 'deprecated' },
  { code: 0xE002B, name: 'Tag Plus Sign', symbol: '⟨T_+⟩', category: 'deprecated' },
  { code: 0xE002C, name: 'Tag Comma', symbol: '⟨T_,⟩', category: 'deprecated' },
  { code: 0xE002D, name: 'Tag Hyphen-Minus', symbol: '⟨T_-⟩', category: 'deprecated' },
  { code: 0xE002E, name: 'Tag Full Stop', symbol: '⟨T_.⟩', category: 'deprecated' },
  { code: 0xE002F, name: 'Tag Solidus', symbol: '⟨T_/⟩', category: 'deprecated' },
  { code: 0xE0030, name: 'Tag Digit Zero', symbol: '⟨T_0⟩', category: 'deprecated' },
  { code: 0xE0031, name: 'Tag Digit One', symbol: '⟨T_1⟩', category: 'deprecated' },
  { code: 0xE0032, name: 'Tag Digit Two', symbol: '⟨T_2⟩', category: 'deprecated' },
  { code: 0xE0033, name: 'Tag Digit Three', symbol: '⟨T_3⟩', category: 'deprecated' },
  { code: 0xE0034, name: 'Tag Digit Four', symbol: '⟨T_4⟩', category: 'deprecated' },
  { code: 0xE0035, name: 'Tag Digit Five', symbol: '⟨T_5⟩', category: 'deprecated' },
  { code: 0xE0036, name: 'Tag Digit Six', symbol: '⟨T_6⟩', category: 'deprecated' },
  { code: 0xE0037, name: 'Tag Digit Seven', symbol: '⟨T_7⟩', category: 'deprecated' },
  { code: 0xE0038, name: 'Tag Digit Eight', symbol: '⟨T_8⟩', category: 'deprecated' },
  { code: 0xE0039, name: 'Tag Digit Nine', symbol: '⟨T_9⟩', category: 'deprecated' },
  { code: 0xE003A, name: 'Tag Colon', symbol: '⟨T_:⟩', category: 'deprecated' },
  { code: 0xE003B, name: 'Tag Semicolon', symbol: '⟨T_;⟩', category: 'deprecated' },
  { code: 0xE003C, name: 'Tag Less-Than Sign', symbol: '⟨T_<⟩', category: 'deprecated' },
  { code: 0xE003D, name: 'Tag Equals Sign', symbol: '⟨T_=⟩', category: 'deprecated' },
  { code: 0xE003E, name: 'Tag Greater-Than Sign', symbol: '⟨T_>⟩', category: 'deprecated' },
  { code: 0xE003F, name: 'Tag Question Mark', symbol: '⟨T_?⟩', category: 'deprecated' },
  { code: 0xE0040, name: 'Tag Commercial At', symbol: '⟨T_@⟩', category: 'deprecated' },
  { code: 0xE0041, name: 'Tag Latin Capital Letter A', symbol: '⟨T_A⟩', category: 'deprecated' },
  { code: 0xE0042, name: 'Tag Latin Capital Letter B', symbol: '⟨T_B⟩', category: 'deprecated' },
  { code: 0xE0043, name: 'Tag Latin Capital Letter C', symbol: '⟨T_C⟩', category: 'deprecated' },
  { code: 0xE0044, name: 'Tag Latin Capital Letter D', symbol: '⟨T_D⟩', category: 'deprecated' },
  { code: 0xE0045, name: 'Tag Latin Capital Letter E', symbol: '⟨T_E⟩', category: 'deprecated' },
  { code: 0xE0046, name: 'Tag Latin Capital Letter F', symbol: '⟨T_F⟩', category: 'deprecated' },
  { code: 0xE0047, name: 'Tag Latin Capital Letter G', symbol: '⟨T_G⟩', category: 'deprecated' },
  { code: 0xE0048, name: 'Tag Latin Capital Letter H', symbol: '⟨T_H⟩', category: 'deprecated' },
  { code: 0xE0049, name: 'Tag Latin Capital Letter I', symbol: '⟨T_I⟩', category: 'deprecated' },
  { code: 0xE004A, name: 'Tag Latin Capital Letter J', symbol: '⟨T_J⟩', category: 'deprecated' },
  { code: 0xE004B, name: 'Tag Latin Capital Letter K', symbol: '⟨T_K⟩', category: 'deprecated' },
  { code: 0xE004C, name: 'Tag Latin Capital Letter L', symbol: '⟨T_L⟩', category: 'deprecated' },
  { code: 0xE004D, name: 'Tag Latin Capital Letter M', symbol: '⟨T_M⟩', category: 'deprecated' },
  { code: 0xE004E, name: 'Tag Latin Capital Letter N', symbol: '⟨T_N⟩', category: 'deprecated' },
  { code: 0xE004F, name: 'Tag Latin Capital Letter O', symbol: '⟨T_O⟩', category: 'deprecated' },
  { code: 0xE0050, name: 'Tag Latin Capital Letter P', symbol: '⟨T_P⟩', category: 'deprecated' },
  { code: 0xE0051, name: 'Tag Latin Capital Letter Q', symbol: '⟨T_Q⟩', category: 'deprecated' },
  { code: 0xE0052, name: 'Tag Latin Capital Letter R', symbol: '⟨T_R⟩', category: 'deprecated' },
  { code: 0xE0053, name: 'Tag Latin Capital Letter S', symbol: '⟨T_S⟩', category: 'deprecated' },
  { code: 0xE0054, name: 'Tag Latin Capital Letter T', symbol: '⟨T_T⟩', category: 'deprecated' },
  { code: 0xE0055, name: 'Tag Latin Capital Letter U', symbol: '⟨T_U⟩', category: 'deprecated' },
  { code: 0xE0056, name: 'Tag Latin Capital Letter V', symbol: '⟨T_V⟩', category: 'deprecated' },
  { code: 0xE0057, name: 'Tag Latin Capital Letter W', symbol: '⟨T_W⟩', category: 'deprecated' },
  { code: 0xE0058, name: 'Tag Latin Capital Letter X', symbol: '⟨T_X⟩', category: 'deprecated' },
  { code: 0xE0059, name: 'Tag Latin Capital Letter Y', symbol: '⟨T_Y⟩', category: 'deprecated' },
  { code: 0xE005A, name: 'Tag Latin Capital Letter Z', symbol: '⟨T_Z⟩', category: 'deprecated' },
  { code: 0xE005B, name: 'Tag Left Square Bracket', symbol: '⟨T_[⟩', category: 'deprecated' },
  { code: 0xE005C, name: 'Tag Reverse Solidus', symbol: '⟨T_\\⟩', category: 'deprecated' },
  { code: 0xE005D, name: 'Tag Right Square Bracket', symbol: '⟨T_]⟩', category: 'deprecated' },
  { code: 0xE005E, name: 'Tag Circumflex Accent', symbol: '⟨T_^⟩', category: 'deprecated' },
  { code: 0xE005F, name: 'Tag Low Line', symbol: '⟨T__⟩', category: 'deprecated' },
  { code: 0xE0060, name: 'Tag Grave Accent', symbol: '⟨T_`⟩', category: 'deprecated' },
  { code: 0xE0061, name: 'Tag Latin Small Letter A', symbol: '⟨T_a⟩', category: 'deprecated' },
  { code: 0xE0062, name: 'Tag Latin Small Letter B', symbol: '⟨T_b⟩', category: 'deprecated' },
  { code: 0xE0063, name: 'Tag Latin Small Letter C', symbol: '⟨T_c⟩', category: 'deprecated' },
  { code: 0xE0064, name: 'Tag Latin Small Letter D', symbol: '⟨T_d⟩', category: 'deprecated' },
  { code: 0xE0065, name: 'Tag Latin Small Letter E', symbol: '⟨T_e⟩', category: 'deprecated' },
  { code: 0xE0066, name: 'Tag Latin Small Letter F', symbol: '⟨T_f⟩', category: 'deprecated' },
  { code: 0xE0067, name: 'Tag Latin Small Letter G', symbol: '⟨T_g⟩', category: 'deprecated' },
  { code: 0xE0068, name: 'Tag Latin Small Letter H', symbol: '⟨T_h⟩', category: 'deprecated' },
  { code: 0xE0069, name: 'Tag Latin Small Letter I', symbol: '⟨T_i⟩', category: 'deprecated' },
  { code: 0xE006A, name: 'Tag Latin Small Letter J', symbol: '⟨T_j⟩', category: 'deprecated' },
  { code: 0xE006B, name: 'Tag Latin Small Letter K', symbol: '⟨T_k⟩', category: 'deprecated' },
  { code: 0xE006C, name: 'Tag Latin Small Letter L', symbol: '⟨T_l⟩', category: 'deprecated' },
  { code: 0xE006D, name: 'Tag Latin Small Letter M', symbol: '⟨T_m⟩', category: 'deprecated' },
  { code: 0xE006E, name: 'Tag Latin Small Letter N', symbol: '⟨T_n⟩', category: 'deprecated' },
  { code: 0xE006F, name: 'Tag Latin Small Letter O', symbol: '⟨T_o⟩', category: 'deprecated' },
  { code: 0xE0070, name: 'Tag Latin Small Letter P', symbol: '⟨T_p⟩', category: 'deprecated' },
  { code: 0xE0071, name: 'Tag Latin Small Letter Q', symbol: '⟨T_q⟩', category: 'deprecated' },
  { code: 0xE0072, name: 'Tag Latin Small Letter R', symbol: '⟨T_r⟩', category: 'deprecated' },
  { code: 0xE0073, name: 'Tag Latin Small Letter S', symbol: '⟨T_s⟩', category: 'deprecated' },
  { code: 0xE0074, name: 'Tag Latin Small Letter T', symbol: '⟨T_t⟩', category: 'deprecated' },
  { code: 0xE0075, name: 'Tag Latin Small Letter U', symbol: '⟨T_u⟩', category: 'deprecated' },
  { code: 0xE0076, name: 'Tag Latin Small Letter V', symbol: '⟨T_v⟩', category: 'deprecated' },
  { code: 0xE0077, name: 'Tag Latin Small Letter W', symbol: '⟨T_w⟩', category: 'deprecated' },
  { code: 0xE0078, name: 'Tag Latin Small Letter X', symbol: '⟨T_x⟩', category: 'deprecated' },
  { code: 0xE0079, name: 'Tag Latin Small Letter Y', symbol: '⟨T_y⟩', category: 'deprecated' },
  { code: 0xE007A, name: 'Tag Latin Small Letter Z', symbol: '⟨T_z⟩', category: 'deprecated' },
  { code: 0xE007B, name: 'Tag Left Curly Bracket', symbol: '⟨T_{⟩', category: 'deprecated' },
  { code: 0xE007C, name: 'Tag Vertical Line', symbol: '⟨T_|⟩', category: 'deprecated' },
  { code: 0xE007D, name: 'Tag Right Curly Bracket', symbol: '⟨T_}⟩', category: 'deprecated' },
  { code: 0xE007E, name: 'Tag Tilde', symbol: '⟨T_~⟩', category: 'deprecated' },
  { code: 0xE007F, name: 'Cancel Tag', symbol: '⟨CTAG⟩', category: 'deprecated' },

  // ===== PRIVATE USE AREA (PUA) - BMP RANGE (U+E000-U+F8FF) - Samples & Detection Ranges =====
  // Note: 6,400 characters total in this range. Detected via range analysis, not individual entries.
  // Some common/known ranges are documented below:
  
  // Font Awesome Legacy (U+F000-U+F2FF approximate)
  { code: 0xF007, name: 'Private Use (Font Awesome: User Icon)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xF015, name: 'Private Use (Font Awesome: Home Icon)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xF0E0, name: 'Private Use (Font Awesome: Envelope Icon)', symbol: '⟨PUA⟩', category: 'deprecated' },
  
  // Material Design Icons (U+E000-U+E3FF approximate)  
  { code: 0xE1FF, name: 'Private Use (Material Icon Range)', symbol: '⟨PUA⟩', category: 'deprecated' },
  
  // Steganography/Hidden Data Detection Indicators (samples from different ranges)
  { code: 0xE100, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xE200, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xE300, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xE400, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xE500, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xE600, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xE700, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xE800, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xE900, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xEA00, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xEB00, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xEC00, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xED00, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xEE00, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xEF00, name: 'Private Use (Potential Steganography)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xF000, name: 'Private Use (Font Awesome Extended)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xF100, name: 'Private Use (Font Awesome Extended)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xF200, name: 'Private Use (Font Awesome Extended)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xF300, name: 'Private Use (Custom)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xF400, name: 'Private Use (Custom)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xF500, name: 'Private Use (Custom)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xF600, name: 'Private Use (Custom)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xF700, name: 'Private Use (Custom)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xF800, name: 'Private Use (Custom)', symbol: '⟨PUA⟩', category: 'deprecated' },
  { code: 0xF8FF, name: 'Private Use (End of BMP PUA)', symbol: '⟨PUA⟩', category: 'deprecated' },

  // ===== FULLWIDTH CHARACTERS (U+FF00-U+FFEF) - CVE-2024-4577 Exploitation Risk =====
  { code: 0xFF0E, name: 'Fullwidth Full Stop', symbol: '．', category: 'space' },
  { code: 0xFF1C, name: 'Fullwidth Less-Than Sign', symbol: '＜', category: 'space' },
  { code: 0xFF1E, name: 'Fullwidth Greater-Than Sign', symbol: '＞', category: 'space' },
  { code: 0xFF3C, name: 'Fullwidth Reverse Solidus', symbol: '＼', category: 'space' },
];

// Create a map for fast lookups
export const INVISIBLE_CHARS_MAP = new Map<number, InvisibleCharacter>(
  INVISIBLE_CHARACTERS.map(char => [char.code, char])
);

// Helper function: Check if character is in Private Use Area
export function isPUACharacter(codePoint: number): boolean {
  return (codePoint >= 0xE000 && codePoint <= 0xF8FF) ||    // BMP PUA
         (codePoint >= 0xF0000 && codePoint <= 0xFFFFD) ||   // Plane 15 PUA
         (codePoint >= 0x100000 && codePoint <= 0x10FFFD);  // Plane 16 PUA
}

// Helper function: Check if character is a Tag Block character
export function isTagBlockCharacter(codePoint: number): boolean {
  return codePoint >= 0xE0000 && codePoint <= 0xE007F;
}

// Helper function: Check if character is Fullwidth (CVE-2024-4577 risk)
export function isFullwidthCharacter(codePoint: number): boolean {
  return codePoint >= 0xFF00 && codePoint <= 0xFFEF;
}

// Helper function: Detect PUA range category
export function getPUACategory(codePoint: number): string {
  if (!isPUACharacter(codePoint)) return 'Not PUA';
  if (codePoint >= 0xE000 && codePoint <= 0xF8FF) return 'BMP_PUA';
  if (codePoint >= 0xF0000 && codePoint <= 0xFFFFD) return 'Plane_15_PUA';
  if (codePoint >= 0x100000 && codePoint <= 0x10FFFD) return 'Plane_16_PUA';
  return 'Unknown';
}

export function detectInvisibleChars(
  text: string,
  showSpaces: boolean = false
): DetectedCharacter[] {
  const results: DetectedCharacter[] = [];
  
  for (let i = 0; i < text.length; i++) {
    const code = text.codePointAt(i);
    if (!code) continue;
    
    const invisibleChar = INVISIBLE_CHARS_MAP.get(code);
    
    if (invisibleChar) {
      // Skip regular spaces if showSpaces is false
      if (!showSpaces && code === 0x0020) {
        continue;
      }
      
      results.push({
        position: i,
        character: invisibleChar,
        length: code > 0xFFFF ? 2 : 1  // Surrogate pairs are 2 units long
      });
      
      // Skip second code unit of surrogate pair
      if (code > 0xFFFF) i++;
    }
  }
  
  return results;
}

export function removeInvisibleChars(text: string, keepSpaces: boolean = true): string {
  let result = '';
  
  for (let i = 0; i < text.length; i++) {
    const code = text.codePointAt(i);
    if (!code) {
      result += text[i];
      continue;
    }
    
    const invisibleChar = INVISIBLE_CHARS_MAP.get(code);
    
    // Keep the character if:
    // - it's not invisible
    // - it's a regular space and we want to keep spaces
    // - it's a line feed (0x000A) - always keep line breaks
    // - it's a carriage return (0x000D) - always keep line breaks
    if (!invisibleChar || 
        (keepSpaces && code === 0x0020) ||
        code === 0x000A ||
        code === 0x000D) {
      // For supplementary plane characters, add both code units
      if (code > 0xFFFF) {
        result += text[i] + text[i + 1];
        i++;
      } else {
        result += text[i];
      }
    } else {
      // Skip surrogate pair for invisible supplementary characters
      if (code > 0xFFFF) i++;
    }
  }
  
  return result;
}

/* eslint-disable no-irregular-whitespace */
export function getExampleText(): string {
  return `This text contains hidden characters!
Try to select this→→invisible→tab
And this has a zero-width space: ​between words
Non-breaking space: here
Soft hyphen: in­visible
Line separator below:

Did you see it?`;
}
/* eslint-enable no-irregular-whitespace */

export function formatUnicodeCode(code: number): string {
  return `U+${code.toString(16).toUpperCase().padStart(5, '0')}`;
}

// Analyze binary patterns in text using ZWSP (0) and ZWNJ (1)
// Only returns a pattern if there are at least 8 bits (1 byte) - enough to encode actual data
export function analyzeBinaryPattern(text: string): {
  hasBinaryPattern: boolean;
  binaryString: string;
  hexValue: string;
  decimalValue: number;
  patternLength: number;
} | null {
  let binaryString = '';
  
  // Extract ZWSP and ZWNJ characters
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code === 0x200B) {
      binaryString += '0'; // ZWSP = 0
    } else if (code === 0x200C) {
      binaryString += '1'; // ZWNJ = 1
    }
  }
  
  // Need at least 8 bits (1 byte) to be considered a meaningful binary pattern
  // Single bits or very short sequences are likely just coincidental
  if (binaryString.length < 8) {
    return null;
  }
  
  // Convert binary to hex and decimal
  const decimalValue = parseInt(binaryString, 2);
  const hexValue = decimalValue.toString(16).toUpperCase();
  
  return {
    hasBinaryPattern: true,
    binaryString,
    hexValue,
    decimalValue,
    patternLength: binaryString.length,
  };
}
